/**
 * Color scales for the national/state choropleth maps. Per the dataviz
 * skill: severity is a status dimension (reuses this app's existing
 * --ok/--advisory/--action/--critical, already used everywhere else —
 * SummaryStrip, ZipDetail, the ZIP-dot map markers), wet-bulb is a
 * sequential magnitude (one hue, light->dark), and deviation-from-normal
 * is diverging (two hues either side of a neutral gray zero).
 *
 * Hex anchors below are taken directly from the dataviz skill's reference
 * palette (blue sequential ramp, blue<->red diverging pair, dark neutral
 * midpoint) rather than hand-picked.
 */

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]) {
  const c = (v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function lerpColor(hexA, hexB, t) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * t));
}

/** Piecewise-linear interpolation across evenly spaced hex stops, t clamped to [0,1]. */
function stopScale(stops) {
  return (t) => {
    const clamped = Math.max(0, Math.min(1, t));
    const n = stops.length - 1;
    const pos = clamped * n;
    const i = Math.min(n - 1, Math.floor(pos));
    return lerpColor(stops[i], stops[i + 1], pos - i);
  };
}

const NO_DATA = 'var(--bg-hover)';

// 4-tier status scale already defined in styles.css — reused as-is rather
// than introducing a second severity palette.
const SEVERITY_STEPS = ['var(--ok)', 'var(--advisory)', 'var(--action)', 'var(--critical)'];

export function severityColor(severity) {
  if (severity == null) return NO_DATA;
  // Aggregated (state-level) severity is a median across counties and can
  // land between tiers (e.g. 1.5) - round to the nearest whole tier for a
  // crisp category rather than blending between unrelated status hues.
  return SEVERITY_STEPS[Math.max(0, Math.min(3, Math.round(severity)))];
}

// Sequential blue ramp, dataviz skill's documented 100->700 steps, light to
// dark. Map fills sit directly on this app's near-black page background
// (--bg) rather than a dedicated light chart surface, so unlike a typical
// dark-mode "flip anchor" ramp, the low end still needs enough luminance to
// read as "a county with data," not a hole in the map - step 250 is the
// floor used here, not the palette's palest step 100.
const WETBULB_STOPS = ['#86b6ef', '#5598e7', '#3987e5', '#256abf', '#1c5cab', '#184f95', '#104281', '#0d366b'];
const wetBulbScale = stopScale(WETBULB_STOPS);

// Advisory heat starts in the mid-70s wet-bulb F and the most severe
// published state thresholds sit around 90F - this domain just sets where
// the ramp saturates, it doesn't define any regulatory trigger itself.
export const WETBULB_DOMAIN = [65, 92];

export function wetBulbColor(wetBulbF) {
  if (wetBulbF == null) return NO_DATA;
  const [lo, hi] = WETBULB_DOMAIN;
  return wetBulbScale((wetBulbF - lo) / (hi - lo));
}

// Diverging blue<->red pair + dark neutral midpoint, all three anchors
// taken directly from the dataviz skill's reference palette (sequential
// blue step 400, dark categorical red, and the documented dark diverging
// midpoint) - each arm is a simple 2-stop lerp from that shared neutral,
// which is monotonic by construction without inventing intermediate hexes.
const DIVERGING_NEUTRAL = '#383835';
const coldScale = stopScale([DIVERGING_NEUTRAL, '#3987e5']);
const hotScale = stopScale([DIVERGING_NEUTRAL, '#e66767']);

// +/- this many degrees F from normal reaches full saturation on either arm.
export const DEVIATION_DOMAIN_F = 12;

export function deviationColor(deviationF) {
  if (deviationF == null) return NO_DATA;
  const t = Math.max(-1, Math.min(1, deviationF / DEVIATION_DOMAIN_F));
  return t < 0 ? coldScale(-t) : hotScale(t);
}

// deviationF (vs. 1991-2020 average) is deliberately left out of METRICS:
// its data source, Open-Meteo's historical archive API, 403s without a
// paid Professional/Enterprise plan - confirmed against their pricing
// page, not a bug here. deviationColor/DEVIATION_DOMAIN_F above are kept
// ready for when that's resolved (see server/src/index.js's comment on
// startHistoricalNormalsLoop) - add a `deviationF` entry back here, same
// shape as wetBulbF below, once historicalNormals.js is actually producing
// data again.
// Today's high: the same diverging blue <-> red anchors as deviationColor,
// centred on 60 °F.
const HIGH_STOPS = ['#3987e5', DIVERGING_NEUTRAL, '#e66767'];
const highScale = stopScale(HIGH_STOPS);
export const HIGH_DOMAIN_F = [20, 100];
// Wind gusts: neutral to amber (sequential); rain: the wet-bulb blue ramp.
const GUST_STOPS = [DIVERGING_NEUTRAL, '#7a6a3c', '#b8892b', '#e9a825'];
const gustScale = stopScale(GUST_STOPS);
export const GUST_DOMAIN_MPH = [0, 60];
export const PRECIP_DOMAIN_IN = [0, 2];
// US AQI categories, in the EPA's own category colours.
const AQI_BANDS = [
  { max: 50, label: 'Good', color: '#00e400' },
  { max: 100, label: 'Moderate', color: '#ffff00' },
  { max: 150, label: 'Unhealthy for sensitive groups', color: '#ff7e00' },
  { max: 200, label: 'Unhealthy', color: '#ff0000' },
  { max: 300, label: 'Very unhealthy', color: '#8f3f97' },
  { max: Infinity, label: 'Hazardous', color: '#7e0023' },
];

const ramp = (scale, [lo, hi]) => (v) => (v == null ? NO_DATA : scale((v - lo) / (hi - lo)));
const gradientCss = (stops) => `linear-gradient(to right, ${stops.join(',')})`;

/**
 * Map colour options. `group: 'env'` are environmental (Open-Meteo model
 * values, US + Canada); `group: 'rules'` is the US work-rule severity.
 * Gradient legends give their ends as a `legendDomain` in the API's units
 * (°F / mph / in) and are formatted in the viewer's units.
 */
export const METRICS = {
  wetBulbF: {
    group: 'env',
    label: 'Wet bulb',
    color: wetBulbColor,
    gradient: gradientCss(WETBULB_STOPS),
    legendDomain: { kind: 'temp', domain: WETBULB_DOMAIN },
  },
  highF: {
    group: 'env',
    label: 'Today’s high',
    color: ramp(highScale, HIGH_DOMAIN_F),
    gradient: gradientCss(HIGH_STOPS),
    legendDomain: { kind: 'temp', domain: HIGH_DOMAIN_F },
  },
  gustMph: {
    group: 'env',
    label: 'Wind gusts (now)',
    color: ramp(gustScale, GUST_DOMAIN_MPH),
    gradient: gradientCss(GUST_STOPS),
    legendDomain: { kind: 'speed', domain: GUST_DOMAIN_MPH },
  },
  precip24In: {
    group: 'env',
    label: 'Rain, next 24 h',
    color: ramp(wetBulbScale, PRECIP_DOMAIN_IN),
    gradient: gradientCss(WETBULB_STOPS),
    legendDomain: { kind: 'precip', domain: PRECIP_DOMAIN_IN },
  },
  aqiPm25: {
    group: 'env',
    label: 'Air quality (PM2.5 AQI, model)',
    color: (v) => (v == null ? NO_DATA : AQI_BANDS.find((b) => v <= b.max).color),
    legend: AQI_BANDS.map((b) => ({ label: b.label, color: b.color })),
  },
  severity: {
    group: 'rules',
    label: 'Work-rule severity (calculated)',
    color: severityColor,
    legend: [
      { label: 'OK', color: 'var(--ok)' },
      { label: 'Advisory', color: 'var(--advisory)' },
      { label: 'Action', color: 'var(--action)' },
      { label: 'Critical', color: 'var(--critical)' },
    ],
  },
};
